// Markdown to safe HTML for note previews. Notes can come from files an agent wrote, so everything
// is sanitized: no scripts, event handlers, frames, forms, styles or javascript: links.
import DOMPurify from 'dompurify';
import { Marked, type Tokens } from 'marked';
import type { LinkTarget } from '@modelviewer/core';

let resolving: (target: string) => LinkTarget | null = () => null;

const escapeHtml = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

const marked = new Marked({ gfm: true, breaks: false });

marked.use({
  extensions: [
    {
      name: 'wikilink',
      level: 'inline',
      start: (src: string) => src.indexOf('[['),
      tokenizer(src: string) {
        const m = /^\[\[([^\]\n|<>]+?)(?:\|([^\]\n<>]+?))?\]\]/.exec(src);
        if (!m) return undefined;
        return { type: 'wikilink', raw: m[0], target: m[1]!.trim(), label: m[2]?.trim() };
      },
      renderer(token: Tokens.Generic) {
        const target = token['target'] as string;
        const resolved = resolving(target);
        const label = (token['label'] as string | undefined) ?? target.replace(/^(note|sketch|migration|context|entity):/i, '');
        return `<a class="mv-wikilink" href="#" data-target="${escapeHtml(target)}" data-kind="${resolved?.kind ?? 'unresolved'}">${escapeHtml(label)}</a>`;
      },
    },
    {
      name: 'tag',
      level: 'inline',
      start(src: string) {
        const m = /(^|[\s(])#[A-Za-z]/.exec(src);
        return m ? m.index + m[1]!.length : undefined;
      },
      tokenizer(src: string) {
        const m = /^#([A-Za-z][\w/-]*)/.exec(src);
        if (!m) return undefined;
        return { type: 'tag', raw: m[0], tag: m[1]!.toLowerCase() };
      },
      renderer(token: Tokens.Generic) {
        const tag = escapeHtml(token['tag'] as string);
        return `<span class="mv-note-tag" data-tag="${tag}">#${tag}</span>`;
      },
    },
  ],
  renderer: {
    link({ href, title, tokens }: Tokens.Link) {
      const text = this.parser.parseInline(tokens);
      const external = /^https?:\/\//i.test(href);
      return `<a href="${escapeHtml(href)}"${title ? ` title="${escapeHtml(title)}"` : ''}${external ? ' target="_blank" rel="noopener noreferrer"' : ''}>${text}</a>`;
    },
  },
});

let hooked = false;

function sanitize(html: string): string {
  if (!hooked) {
    hooked = true;
    // Links that open elsewhere never get access to this page.
    DOMPurify.addHook('afterSanitizeAttributes', (node) => {
      if (node.tagName === 'A' && node.getAttribute('target')) {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
      }
    });
  }
  return DOMPurify.sanitize(html, {
    ADD_ATTR: ['target'],
    FORBID_TAGS: ['style', 'iframe', 'frame', 'object', 'embed', 'form', 'input', 'button', 'textarea', 'select', 'meta', 'link', 'base', 'svg', 'math'],
    FORBID_ATTR: ['style', 'srcset'],
  });
}

/** Renders a note. `resolve` decides how each wiki link is shown (and whether it is broken). */
export function renderMarkdown(text: string, resolve: (target: string) => LinkTarget | null): string {
  resolving = resolve;
  try {
    return sanitize(marked.parse(text, { async: false }));
  } finally {
    resolving = () => null;
  }
}
